import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, Loader2 } from 'lucide-react'
import type { ClaudeAccount, ClaudeLogin, UsageWindow } from '@one-deployment/api'
import { Badge, Button, Card, Dialog, Input, Label, Menu, PageHeader, Segmented, SkeletonRows, Table, Td, Tr } from '@/components/ui'
import { EmptyState, ErrorState } from '@/components/status'
import { RoleGate } from '@/components/RoleGate'
import { del, enc, get, patch, post } from '@/lib/api'
import { dateTime } from '@/lib/format'
import { qk } from '@/lib/query'
import { useClaudeAccounts } from './accounts'
import { ask, askText } from '@/components/ask'
import { Checkbox } from '@/components/shadcn/checkbox'
import { useProjects } from '@/features/shell/useOverview'

const tokens = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 })

/**
 * One usage window as a bar: orange from 75%, red when spent. The ceiling is only known once the
 * account has hit a limit; until then the bar is empty and the tooltip says what it used.
 */
function UsageBar({ w, limited }: { w: UsageWindow; limited: boolean }) {
  const pct = w.percent === null ? (limited ? 100 : null) : Math.max(0, Math.min(100, Math.round(w.percent)))
  const tone = pct === null ? '' : pct >= 100 ? 'bg-err' : pct >= 75 ? 'bg-warn' : 'bg-fg-muted'
  return (
    <span className="inline-flex flex-col gap-0.5" title={w.resetsAt ? `Resets ${dateTime(w.resetsAt)}` : `${tokens.format(w.used)} tokens used`}>
      <span className="w-24 text-right text-2xs text-fg-faint">{w.resetsAt ? resetIn(w.resetsAt) : '\u00a0'}</span>
      <span className="flex items-center gap-2">
        <span className="h-1.5 w-24 overflow-hidden rounded-full bg-surface-3">
          {pct !== null && <span className={`block h-full rounded-full ${tone}`} style={{ width: `${pct}%` }} />}
        </span>
        <span className={`w-9 text-xs tabular-nums ${pct !== null && pct >= 100 ? 'text-err' : pct !== null && pct >= 75 ? 'text-warn' : 'text-fg-subtle'}`}>{pct === null ? '—' : `${pct}%`}</span>
      </span>
    </span>
  )
}

/** "resets in 2h 10m" within a day, else "resets Thu 09:00". */
function resetIn(at: number) {
  const ms = at - Date.now()
  if (ms <= 0) return 'resets now'
  if (ms < 86_400_000) {
    const h = Math.floor(ms / 3_600_000), m = Math.floor((ms % 3_600_000) / 60_000)
    return `resets in ${h ? `${h}h ` : ''}${m}m`
  }
  return `resets ${new Date(at).toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' })}`
}

/** Paste the output of `claude setup-token`. It goes in once and never comes back out. */
function PasteForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient()
  const [label, setLabel] = useState('')
  const [token, setToken] = useState('')
  const m = useMutation({
    mutationFn: () => post<ClaudeAccount>('/api/claude-accounts', { label, token: token.trim() }),
    onSuccess: () => { setToken(''); void qc.invalidateQueries({ queryKey: qk.claudeAccounts() }); onDone() },
  })
  return (
    <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); m.mutate() }}>
      <div><Label htmlFor="ca-label">Name</Label><Input id="ca-label" required maxLength={60} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="TriSof Agent" autoFocus /></div>
      <div>
        <Label htmlFor="ca-token">Token</Label>
        <Input id="ca-token" required type="password" autoComplete="off" spellCheck={false} value={token} onChange={(e) => setToken(e.target.value)} placeholder="sk-ant-oat01-…" />
        <p className="mt-1 text-2xs text-fg-faint">Run <code>claude setup-token</code> on any machine signed in to the subscription and paste what it prints. API keys are refused: Claude runs only on subscriptions.</p>
      </div>
      {m.error && <ErrorState error={m.error} compact />}
      <div className="flex justify-end gap-2"><Button onClick={onDone}>Cancel</Button><Button type="submit" variant="primary" disabled={m.isPending}>Add account</Button></div>
    </form>
  )
}

/**
 * Sign in from the dashboard: a box runs `claude setup-token` in an existing project
 * container; the sign-in link appears here, the code goes back the same way,
 * and the token goes straight from the box to the Worker (never this page).
 */
/** A wait the person can see is a wait: a bare "Starting…" read as stuck. */
function Waiting({ text, hint }: { text: string; hint: string }) {
  const [secs, setSecs] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setSecs((s) => s + 1), 1000)
    return () => clearInterval(t)
  }, [])
  return (
    <div className="flex items-start gap-2.5 rounded-md border border-border bg-surface-2 px-3 py-2.5">
      <Loader2 size={16} className="mt-0.5 shrink-0 animate-spin text-info" />
      <div>
        <p>{text}… <span className="tabular-nums text-fg-faint">{secs}s</span></p>
        <p className="text-2xs text-fg-faint">{hint}</p>
      </div>
    </div>
  )
}

function SignInFlow({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient()
  const [label, setLabel] = useState('')
  const [loginId, setLoginId] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const start = useMutation({ mutationFn: () => post<ClaudeLogin>('/api/claude-accounts/logins', { label }), onSuccess: (l) => setLoginId(l.id) })
  const login = useQuery({
    queryKey: qk.claudeLogin(loginId ?? ''),
    queryFn: ({ signal }) => get<ClaudeLogin>(`/api/claude-accounts/logins/${enc(loginId!)}`, signal),
    enabled: !!loginId,
    // The hub says when it moves; polling covers a dropped socket.
    refetchInterval: (q) => (q.state.data && ['done', 'failed', 'timeout', 'cancelled'].includes(q.state.data.state) ? false : 2000),
  })
  const send = useMutation({ mutationFn: () => post<ClaudeLogin>(`/api/claude-accounts/logins/${enc(loginId!)}/code`, { code: code.trim() }), onSuccess: (l) => qc.setQueryData(qk.claudeLogin(l.id), l) })
  const cancel = () => { if (loginId && login.data && ['starting', 'waiting_code', 'code_sent'].includes(login.data.state)) void post(`/api/claude-accounts/logins/${enc(loginId)}/cancel`); onDone() }
  const l = login.data

  if (!loginId) {
    return (
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); start.mutate() }}>
        <div><Label htmlFor="cl-label">Name</Label><Input id="cl-label" required maxLength={60} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="TriSof Agent" autoFocus /></div>
        {start.error && <ErrorState error={start.error} compact />}
        <div className="flex justify-end gap-2"><Button onClick={onDone}>Cancel</Button><Button type="submit" variant="primary" disabled={start.isPending}>Start sign-in</Button></div>
      </form>
    )
  }
  return (
    <div className="space-y-3 text-sm">
      {/* Without this a failing poll looked exactly like a slow start. */}
      {!l && login.error && <ErrorState error={login.error} compact />}
      {(!l && !login.error) || l?.state === 'starting' ? <Waiting text={`Starting the sign-in on ${l?.serverId ?? 'a server'}`} hint="The link to sign in to Claude appears here, usually within a few seconds." /> : null}
      {l?.state === 'waiting_code' && l.url && (
        <>
          <p>1. Open the sign-in page and sign in with the subscription's Claude account:</p>
          <a href={l.url} target="_blank" rel="noopener noreferrer" className="block truncate rounded-md border border-border bg-surface-2 px-2 py-1.5 text-xs text-accent-hover hover:underline">Sign in to Claude ↗</a>
          <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); send.mutate() }}>
            <Label htmlFor="cl-code">2. Paste the code the page shows</Label>
            <Input id="cl-code" required autoComplete="off" spellCheck={false} value={code} onChange={(e) => setCode(e.target.value)} />
            {send.error && <ErrorState error={send.error} compact />}
            <div className="flex justify-end gap-2"><Button onClick={cancel}>Cancel</Button><Button type="submit" variant="primary" disabled={send.isPending}>Finish sign-in</Button></div>
          </form>
        </>
      )}
      {l?.state === 'code_sent' && <Waiting text="Checking the code" hint="Usually a few seconds." />}
      {l?.state === 'done' && (
        <>
          <p className="text-ok">Signed in. “{l.label}” is ready to use.</p>
          <div className="flex justify-end"><Button variant="primary" onClick={() => { void qc.invalidateQueries({ queryKey: qk.claudeAccounts() }); onDone() }}>Done</Button></div>
        </>
      )}
      {l && ['failed', 'timeout', 'cancelled'].includes(l.state) && (
        <>
          <p className="text-warn">{l.error ?? (l.state === 'timeout' ? 'The sign-in timed out.' : l.state === 'cancelled' ? 'Cancelled.' : 'The sign-in failed.')}</p>
          <div className="flex justify-end gap-2"><Button onClick={onDone}>Close</Button><Button variant="primary" onClick={() => { setLoginId(null); setCode('') }}>Try again</Button></div>
        </>
      )}
      {l && ['starting', 'code_sent'].includes(l.state) && <div className="flex justify-end"><Button onClick={cancel}>Cancel</Button></div>}
    </div>
  )
}

function AddDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [how, setHow] = useState<'sign_in' | 'paste'>('sign_in')
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Add a Claude account">
      <div className="mb-3"><Segmented value={how} onChange={setHow} options={[{ value: 'sign_in', label: 'Sign in' }, { value: 'paste', label: 'Paste a token' }]} /></div>
      {open && (how === 'sign_in' ? <SignInFlow onDone={() => onOpenChange(false)} /> : <PasteForm onDone={() => onOpenChange(false)} />)}
    </Dialog>
  )
}

/** Settings → Claude accounts: the subscriptions sessions run on (0008). */
export function ClaudeAccountsPage() {
  const qc = useQueryClient()
  const { data, error, refetch } = useClaudeAccounts()
  const [adding, setAdding] = useState(false)
  const [assigning, setAssigning] = useState<ClaudeAccount | null>(null)
  const projects = useProjects().data?.projects ?? []
  const projectName = (k: string) => projects.find((x) => x.key === k)?.name ?? k
  const act = useMutation({
    mutationFn: (f: () => Promise<unknown>) => f(),
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.claudeAccounts() }),
  })
  const rename = (a: ClaudeAccount) => {
    void askText({ title: 'Rename account', value: a.label, confirm: 'Rename' }).then((label) => {
      if (label && label !== a.label) act.mutate(() => patch(`/api/claude-accounts/${enc(a.id)}`, { label }, a.version))
    })
  }
  return (
    <div>
      <PageHeader
        title="Claude accounts"
       
        actions={<RoleGate action="claude_accounts.manage"><Button variant="primary" onClick={() => setAdding(true)}>Add account</Button></RoleGate>}
      />
      <div className="p-5">
        {error && !data ? <ErrorState error={error} retry={() => void refetch()} /> : !data ? <SkeletonRows rows={4} /> : data.accounts.length === 0 ? (
          <EmptyState icon={<KeyRound size={20} />} title="No Claude accounts yet" />
        ) : (
          <Card>
            <Table head={['Account', '5 hours', 'Week', 'Projects', 'Last used', '']}>
              {data.accounts.map((a) => {
                const limited = a.state === 'limited'
                return (
                <Tr key={a.id} className={limited ? 'bg-err/10' : undefined}>
                  <Td>
                    <span className="flex items-center gap-2" title={limited ? `Limit reached · ${a.resetsAt ? `resets ${dateTime(a.resetsAt)}` : 'retried every 15 min'}` : undefined}>
                      <span className={limited ? 'text-err' : undefined}>{a.label}</span>
                      {a.isDefault && <Badge tone="accent">default</Badge>}
                      {(a.state === 'invalid' || !a.last4) && <Badge tone="err">needs sign-in</Badge>}
                    </span>
                  </Td>
                  <Td><UsageBar w={a.usage.fiveHour} limited={limited} /></Td>
                  <Td><UsageBar w={a.usage.week} limited={limited && a.usage.week.percent === null && a.usage.fiveHour.percent === null} /></Td>
                  <Td className="max-w-64">
                    {a.isDefault ? <span className="text-fg-subtle">All projects</span> : (
                      <RoleGate action="claude_accounts.manage" fallback={<span className="truncate text-fg-subtle">{a.projects.map(projectName).join(', ') || 'None'}</span>}>
                        <button type="button" onClick={() => setAssigning(a)} className="-ml-1.5 block max-w-full truncate rounded-md px-1.5 py-0.5 text-left hover:bg-hover" title="Assign projects">
                          {a.projects.length ? <span className="text-fg-muted">{a.projects.map(projectName).join(', ')}</span> : <span className="text-warn">Assign projects</span>}
                        </button>
                      </RoleGate>
                    )}
                  </Td>
                  <Td className="text-fg-subtle">{a.lastUsedAt ? dateTime(a.lastUsedAt) : 'never'}</Td>
                  <Td>
                    <RoleGate action="claude_accounts.manage">
                      <Menu trigger={<Button variant="ghost" size="sm" aria-label={`Actions for ${a.label}`}>⋯</Button>} items={[
                        { label: 'Rename', onSelect: () => rename(a) },
                        ...(a.isDefault ? [] : [{ label: 'Assign projects…', onSelect: () => setAssigning(a) }]),
                        ...(a.isDefault ? [] : [{ label: 'Make default', onSelect: () => act.mutate(() => post(`/api/claude-accounts/${enc(a.id)}/default`)) }]),
                        'sep' as const,
                        { label: 'Remove', danger: true, onSelect: () => void ask({ title: `Remove “${a.label}”?`, body: 'Sessions on it switch at their next Claude start.', confirm: 'Remove', danger: true }).then((ok) => ok && act.mutate(() => del(`/api/claude-accounts/${enc(a.id)}`))) },
                      ]} />
                    </RoleGate>
                  </Td>
                </Tr>
                )
              })}
            </Table>
          </Card>
        )}
        {act.error && <div className="pt-3"><ErrorState error={act.error} compact /></div>}
      </div>
      <AddDialog open={adding} onOpenChange={setAdding} />
      {assigning && <ProjectsDialog account={assigning} projects={projects} onClose={() => setAssigning(null)} />}
    </div>
  )
}

/** Pick the projects a (non-default) account serves; at a limit, sessions move only to accounts serving their project. */
function ProjectsDialog({ account, projects, onClose }: { account: ClaudeAccount; projects: { key: string; name: string }[]; onClose: () => void }) {
  const qc = useQueryClient()
  const [picked, setPicked] = useState<string[]>(account.projects)
  const save = useMutation({
    mutationFn: () => patch(`/api/claude-accounts/${enc(account.id)}`, { projects: picked }, account.version),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.claudeAccounts() }); onClose() },
  })
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Projects for ${account.label}`}
      description="Sessions in these projects can use this account, and move to it when another hits its limit. The default account serves every project.">
      <div className="max-h-80 space-y-1 overflow-auto">
        {projects.map((p) => (
          <label key={p.key} className="flex h-8 cursor-pointer items-center gap-2 rounded-md px-2 text-sm hover:bg-hover">
            <Checkbox checked={picked.includes(p.key)} onCheckedChange={(v) => setPicked(v === true ? [...picked, p.key] : picked.filter((k) => k !== p.key))} />
            {p.name}
          </label>
        ))}
      </div>
      {save.error && <ErrorState error={save.error} compact />}
      <div className="flex justify-end gap-2">
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save'}</Button>
      </div>
    </Dialog>
  )
}
